import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X } from 'lucide-react';
import { portfolioData } from '@/data/mock';
import { ModeToggle } from '@/components/ModeToggle';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -60% 0px' }
    );

    const sectionIds = portfolioData.navLinks.map((l) => l.href.slice(1));
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = useCallback((e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: 'smooth' });
    }
    setMenuOpen(false);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-sm transition-shadow duration-300 ${
        scrolled ? 'shadow-[0_1px_3px_rgba(0,0,0,0.08)] dark:shadow-[0_1px_3px_rgba(255,255,255,0.05)]' : ''
      }`}
    >
      <div className="max-w-[864px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="font-bold text-lg text-[#0A0A0A] dark:text-white tracking-tight hover:text-[#2563EB] transition-colors duration-200"
        >
          GS<span className="text-[#2563EB]">.</span>
        </a>

        <nav className="hidden md:flex items-center gap-1" aria-label="Navegação principal">
          {portfolioData.navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors duration-200 ${
                activeSection === link.href.slice(1)
                  ? 'text-[#2563EB] bg-[#2563EB]/[0.06]'
                  : 'text-[#6B7280] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/[0.06]'
              }`}
            >
              {link.label}
            </a>
          ))}

          <ModeToggle />
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <ModeToggle />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 -mr-2 text-[#0A0A0A] dark:text-white hover:text-[#2563EB] transition-colors duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
            aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          menuOpen ? 'max-h-[400px] border-t border-gray-100 dark:border-white/10' : 'max-h-0'
        }`}
      >
        <nav className="max-w-[864px] mx-auto px-4 py-2 flex flex-col">
          {portfolioData.navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                activeSection === link.href.slice(1)
                  ? 'text-[#2563EB] bg-[#2563EB]/[0.06]'
                  : 'text-[#6B7280] hover:text-[#0A0A0A] dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/[0.06]'
              }`}
            >
              {link.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
};

export default Header;
